import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
  Phone, PhoneOff, PhoneIncoming, PhoneForwarded, Mic, MicOff,
  Pause, Play, Grid, Clock, History, Settings, Check, X, PhoneCall,
  Signal, Wifi, ChevronDown, Monitor, Globe, Radio
} from 'lucide-react';
import { useSoftphoneContext } from '../softphone/index';
import { useThemeMode } from '../../../partials/layout/theme-mode/ThemeModeProvider';

interface NavbarSoftphoneProps {
  className?: string;
  dropUp?: boolean;
  forceDark?: boolean;
  variant?: 'all' | 'status-only' | 'dialer-only';
}

const NavbarSoftphone: React.FC<NavbarSoftphoneProps> = ({ 
  className = '', 
  dropUp = false, 
  forceDark = false,
  variant = 'all'
}) => {
  const context = useSoftphoneContext();
  const { mode } = useThemeMode();
  const isSystemDark = mode === 'dark' || document.documentElement.getAttribute('data-bs-theme') === 'dark';
  const isDark = forceDark || isSystemDark;

  const [telephonyMode, setTelephonyMode] = useState<'desktop' | 'web'>(() => {
    return (localStorage.getItem('telephony-device-mode') as 'desktop' | 'web') || 'desktop';
  });

  useEffect(() => {
    const modeHandler = (e: any) => {
      if (e?.detail) setTelephonyMode(e.detail);
    };
    window.addEventListener('telephony-device-mode-changed', modeHandler);
    return () => window.removeEventListener('telephony-device-mode-changed', modeHandler);
  }, []);

  const {
    handleEndCall, handleMicMute, localStatePhone, activeChannel,
    handleCallTransfer, handleCallAttendedTransfer, handleCall,
    dialState, setDialState, handleHold, handleAnswer, flowRoute, calls
  } = (context as any) || {};

  const activeCallState = localStatePhone?.displayCalls?.[activeChannel || 0] || {};
  const isMuted = activeCallState.muted || false;
  const isInCall = activeCallState.inCall || false;
  const isHold = activeCallState.hold || false;
  const { inAnswerTransfer, inConference, inTransfer, transferControl } = activeCallState;
  const showTransferControls = inAnswerTransfer && !inConference && inTransfer && transferControl;
  const [duration, setDuration] = useState(0);
  const [showDialpad, setShowDialpad] = useState(false);

  const isSipRegistered = localStatePhone?.connectedPhone ?? false;
  const isSipConnecting = localStatePhone?.connectingPhone ?? false;
  const incomingCalls = localStatePhone?.phoneCalls || [];

  useEffect(() => {
    let interval: any;
    if (isInCall) {
      interval = setInterval(() => { setDuration(prev => prev + 1); }, 1000);
    } else {
      setDuration(0);
    }
    return () => clearInterval(interval);
  }, [isInCall]);

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const onMuteToggle = () => { if (handleMicMute) handleMicMute(); };
  const onHoldToggle = () => {
    if (handleHold && activeCallState.sessionId) handleHold(activeCallState.sessionId, isHold);
  };
  const onHangup = () => { if (handleEndCall) handleEndCall({ persist: () => {} } as any); };
  const onCall = () => { if (dialState && flowRoute) flowRoute.call(dialState); };
  const onBlindTransfer = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!dialState) { alert('Masukkan nomor tujuan.'); return; }
    if (handleCallTransfer) { handleCallTransfer(dialState); if (setDialState) setDialState(''); }
  };
  const onAttendedTransfer = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!dialState) { alert('Masukkan nomor tujuan.'); return; }
    if (handleCallAttendedTransfer) { handleCallAttendedTransfer('transfer', dialState); if (setDialState) setDialState(''); }
  };
  const onKeyPress = (key: string) => {
    if (isInCall && flowRoute?.activeCall) {
      flowRoute.activeCall.sendDTMF(key);
    } else {
      if (setDialState) setDialState((prev: string) => (prev || '') + key);
    }
  };

  const glassBorder = isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)';
  const txt = isDark ? '#ffffff' : '#1e293b';
  const muted = isDark ? 'rgba(255, 255, 255, 0.65)' : '#64748b';

  const iconBtnStyle = (active = false, activeBg = 'rgba(239,68,68,0.18)') => ({
    width: 34,
    height: 34,
    padding: 0,
    borderRadius: 8,
    border: glassBorder,
    background: active ? activeBg : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
    color: txt,
    boxShadow: 'none',
    transition: 'all 0.15s ease'
  });

  const renderStatusBadge = () => {
    if (telephonyMode === 'desktop') {
      return (
        <div
          className="d-flex align-items-center justify-content-center gap-2 rounded-3"
          style={{
            height: 38,
            padding: dropUp ? '0 10px' : '0 18px',
            minWidth: dropUp ? 'auto' : '145px',
            background: 'rgba(139,92,246,0.14)',
            border: '1px solid rgba(196,181,253,0.4)',
            color: '#ddd6fe'
          }}
          title="External Softphone (SIP/Deskphone) terhubung ke server dialer"
        >
          <Radio size={14} style={{ color: '#c4b5fd' }} />
          {!dropUp && (
            <span className="fw-bold text-uppercase" style={{ fontSize: 11, letterSpacing: '0.6px', whiteSpace: 'nowrap' }}>
              Signaling Online
            </span>
          )}
        </div>
      );
    }

    return (
      <div
        className="d-flex align-items-center justify-content-center gap-1.5 rounded-3"
        style={{
          height: 38,
          padding: dropUp ? '0 10px' : '0 18px',
          minWidth: dropUp ? 'auto' : '95px',
          background: isSipRegistered
            ? 'rgba(16,185,129,0.14)'
            : isSipConnecting
              ? 'rgba(245,158,11,0.14)'
              : 'rgba(239,68,68,0.14)',
          border: isSipRegistered
            ? '1px solid rgba(110,231,183,0.45)'
            : isSipConnecting
              ? '1px solid rgba(252,211,77,0.45)'
              : '1px solid rgba(252,165,165,0.45)',
          color: isSipRegistered ? '#34d399' : isSipConnecting ? '#fbbf24' : '#f87171'
        }}
        title={isSipRegistered ? 'WebRTC Softphone Registered' : isSipConnecting ? 'Mendaftarkan SIP...' : 'SIP Disconnected (Offline)'}
      >
        <Wifi size={14} />
        {!dropUp && (
          <span className="fw-bold text-uppercase" style={{ fontSize: 11, letterSpacing: '0.6px', whiteSpace: 'nowrap' }}>
            {isSipRegistered ? 'Ready' : isSipConnecting ? 'Connecting...' : 'Offline'}
          </span>
        )}
      </div>
    );
  };

  const renderDialerOrInCall = () => {
    if (isInCall) {
      if (dropUp) {
        return (
          <div className="d-flex align-items-center gap-1.5">
            {/* Mobile In-Call Red Hangup Button - Same exact size as green phone button */}
            <button
              className="btn d-flex align-items-center justify-content-center px-3 rounded-3 fw-bold shadow-sm"
              style={{
                height: 38,
                fontSize: '12px',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#ffffff',
                border: 'none',
              }}
              title="Tutup Panggilan" 
              onClick={onHangup}
            >
              <PhoneOff size={15} className="flex-shrink-0" />
            </button>
          </div>
        );
      }

      return (
        <div className="d-flex align-items-center gap-2">
          {/* Hangup */}
          <button
            className="btn d-flex align-items-center gap-2 fw-bold rounded-2"
            style={{ height: 34, padding: '0 14px', background: 'linear-gradient(135deg,#ef4444,#dc2626)', border: 'none', color: '#fff', boxShadow: '0 3px 12px rgba(239,68,68,0.45)', fontSize: 12 }}
            title="Tutup Panggilan" onClick={onHangup}
          >
            <PhoneOff size={13} className="text-white" />
            <span>End</span>
          </button>

          {/* Active call info pill */}
          <div className="d-flex align-items-center gap-2 px-3 rounded-3"
            style={{ height: 34, background: 'rgba(16,185,129,0.12)', border: '1.5px solid rgba(16,185,129,0.35)' }}>
            <span className="bullet bullet-dot animate__animated animate__pulse animate__infinite" style={{ width: 6, height: 6, background: '#10b981', flexShrink: 0 }}></span>
            <span className="fw-semibold font-monospace" style={{ fontSize: 12, maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#34d399' }}>
              {activeCallState.callNumber || dialState || 'Connected'}
            </span>
            <span className="fw-boldest font-monospace px-2 rounded-2" style={{ fontSize: 11, background: 'rgba(16,185,129,0.20)', color: '#34d399', letterSpacing: '0.04em', flexShrink: 0 }}>
              {formatDuration(duration)}
            </span>
          </div>

          {/* Mute */}
          <button
            className="btn d-flex align-items-center justify-content-center"
            title={isMuted ? 'Unmute' : 'Mute'}
            style={isMuted ? { ...iconBtnStyle(), background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' } : iconBtnStyle()}
            onClick={onMuteToggle}
          >
            {isMuted ? <MicOff size={14} style={{ color: '#fca5a5' }} /> : <Mic size={14} style={{ color: txt }} />}
          </button>

          {/* Hold */}
          <button
            className="btn d-flex align-items-center justify-content-center"
            title={isHold ? 'Resume' : 'Hold'}
            style={isHold ? { ...iconBtnStyle(), background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)' } : iconBtnStyle()}
            onClick={onHoldToggle}
          >
            {isHold ? <Play size={14} style={{ color: '#fcd34d' }} /> : <Pause size={14} style={{ color: txt }} />}
          </button>

          {/* DTMF Dialpad */}
          <button
            className="btn d-flex align-items-center justify-content-center"
            title="DTMF Keypad"
            style={showDialpad ? { ...iconBtnStyle(true, 'rgba(59,130,246,0.25)'), border: '1px solid rgba(59,130,246,0.5)' } : iconBtnStyle()}
            onClick={() => setShowDialpad(v => !v)}
          >
            <Grid size={14} style={{ color: showDialpad ? '#60a5fa' : txt }} />
          </button>

          {/* Transfer dropdown */}
          <div className={clsx("dropdown", dropUp && "dropup")}>
            <button
              className="btn d-flex align-items-center gap-2 rounded-2 fw-semibold"
              style={{ height: 34, padding: '0 12px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.40)', color: '#93c5fd', fontSize: 12 }}
              data-bs-toggle="dropdown"
            >
              <PhoneForwarded size={13} />
              <span>Transfer</span>
              <ChevronDown size={11} className="opacity-75" />
            </button>
            <ul className="dropdown-menu shadow-lg rounded-4 py-2 border-0 animate__animated animate__fadeIn animate__faster" style={{ zIndex: 1050, minWidth: 200, boxShadow: '0 12px 36px rgba(0,0,0,0.18)' }}>
              {!showTransferControls ? (
                <>
                  <li><a className="dropdown-item py-2 fs-7 fw-semibold" href="#" onClick={onBlindTransfer}>⚡ Blind Transfer</a></li>
                  <li><a className="dropdown-item py-2 fs-7 fw-semibold" href="#" onClick={onAttendedTransfer}>🎧 Attended Transfer</a></li>
                </>
              ) : (
                <>
                  <li><a className="dropdown-item py-2 fs-7 fw-semibold" href="#" onClick={(e) => { e.preventDefault(); handleCallAttendedTransfer?.('merge', {}); }}>Merge Call</a></li>
                  <li><a className="dropdown-item py-2 fs-7 fw-semibold" href="#" onClick={(e) => { e.preventDefault(); handleCallAttendedTransfer?.('swap', {}); }}>Swap Caller</a></li>
                  <li><a className="dropdown-item py-2 fs-7 fw-semibold" href="#" onClick={(e) => { e.preventDefault(); handleCallAttendedTransfer?.('finish', {}); }}>Pass Call</a></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><a className="dropdown-item py-2 fs-7 fw-semibold text-danger" href="#" onClick={(e) => { e.preventDefault(); handleCallAttendedTransfer?.('cancel', {}); }}>Cancel</a></li>
                </>
              )}
            </ul>
          </div>
        </div>
      );
    }

    if (telephonyMode === 'desktop') {
      return null;
    }

    if (dropUp) {
      return (
        <button
          className="btn d-flex align-items-center justify-content-center px-3 rounded-3 fw-bold shadow-sm"
          style={{
            height: 38,
            fontSize: '12px',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: '#ffffff',
            border: 'none',
          }}
          onClick={() => setShowDialpad(true)}
          title="Buka Keypad Dialpad"
        >
          <Phone size={15} className="flex-shrink-0" />
          {dialState ? <span className="text-truncate ms-1.5" style={{ maxWidth: '80px' }}>{dialState}</span> : null}
        </button>
      );
    }

    return (
      <div
        className="d-flex align-items-center rounded-3 overflow-hidden"
        style={{ height: 42, border: glassBorder, background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)' }}
      >
        <input
          type="text"
          className="form-control border-0 bg-transparent fw-semibold text-center"
          style={{ boxShadow: 'none', fontSize: 13, color: txt, caretColor: '#60a5fa', width: '130px' }}
          placeholder="Nomor..."
          value={dialState || ''}
          onChange={(e) => setDialState && setDialState(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onCall(); }}
        />
        <button
          className="btn d-flex align-items-center justify-content-center"
          style={{ width: 36, height: 42, padding: 0, background: 'rgba(255,255,255,0.06)', borderLeft: glassBorder, borderRight: 'none', borderTop: 'none', borderBottom: 'none', color: muted }}
          title="Dialpad"
          onClick={() => setShowDialpad(v => !v)}
        >
          <Grid size={14} />
        </button>
        <button
          className="btn d-flex align-items-center justify-content-center"
          style={{ width: 44, height: 42, padding: 0, background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', minWidth: 44, boxShadow: 'none' }}
          title="Panggil" onClick={onCall}
        >
          <Phone size={14} className="text-white" />
        </button>
      </div>
    );
  };

  return (
    <div className={clsx("navbar-softphone-container d-inline-flex align-items-center", className)}>
      {/* ── INCOMING CALL ALERT ── */}
      {incomingCalls.length > 0 && (
        <div
          className="d-flex align-items-center gap-2 px-3 rounded-3 shadow-lg animate__animated animate__pulse animate__infinite"
          style={{
            height: 42,
            background: 'linear-gradient(135deg, rgba(16,185,129,0.95), rgba(5,150,105,0.95))',
            color: '#fff',
            border: '1.5px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 20px rgba(16,185,129,0.5)'
          }}
        >
          <div className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25" style={{ width: 26, height: 26 }}>
            <PhoneIncoming size={14} className="text-white" />
          </div>
          <div className="d-flex flex-column leading-none">
            <span className="fw-bolder" style={{ fontSize: 12 }}>Panggilan Masuk</span>
            <span className="font-monospace text-white-50" style={{ fontSize: 10 }}>{incomingCalls[0].callerNumber || 'Nasabah'}</span>
          </div>
          <button
            className="btn btn-sm btn-icon btn-white rounded-circle ms-2"
            style={{ width: 28, height: 28, minWidth: 28, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}
            title="Terima Panggilan"
            onClick={() => { if (flowRoute) flowRoute.answer(incomingCalls[0].sessionId); }}
          >
            <Check size={14} className="text-success fw-bold" />
          </button>
          <button
            className="btn btn-sm btn-icon rounded-circle"
            style={{ width: 28, height: 28, minWidth: 28, background: 'rgba(0,0,0,0.2)', color: '#fff' }}
            title="Tolak Panggilan"
            onClick={() => { if (flowRoute) flowRoute.hungup(incomingCalls[0].sessionId); }}
          >
            <X size={11} />
          </button>
        </div>
      )}

      {/* RENDER BY VARIANT */}
      {variant === 'status-only' ? (
        renderStatusBadge()
      ) : variant === 'dialer-only' ? (
        renderDialerOrInCall()
      ) : (
        <div className="d-flex align-items-center gap-2 gap-sm-3">
          {renderStatusBadge()}
          {renderDialerOrInCall()}
        </div>
      )}

      {/* ── FLOATING DIALPAD MODAL POPUP ── */}
      {incomingCalls.length === 0 && showDialpad && (telephonyMode === 'web' || isInCall) && (
        <>
          {dropUp && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100"
              style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}
              onClick={() => setShowDialpad(false)}
            />
          )}
          <div
            className={clsx(
              "rounded-4 p-4 animate__animated animate__faster shadow-lg",
              dropUp ? "position-fixed animate__fadeInUp" : "position-absolute top-100 mt-2 animate__fadeIn"
            )}
            style={dropUp ? {
              zIndex: 1060,
              width: 'min(290px, 90vw)',
              bottom: '80px',
              left: 0,
              right: 0,
              margin: '0 auto',
              background: isDark ? '#1e1e2d' : '#ffffff',
              boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.15)',
            } : {
              zIndex: 1050,
              width: 240,
              background: isDark ? '#1e1e2d' : '#ffffff',
              boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
              border: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="fw-boldest text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: '0.8px' }}>
                {isInCall ? 'DTMF Keypad' : 'Keypad Dialpad'}
              </span>
              <button className="btn btn-sm btn-icon" onClick={() => setShowDialpad(false)}><X size={15} /></button>
            </div>

            {dropUp && !isInCall && (
              <div className="mb-3 px-3 py-2 rounded-3 text-center fw-boldest font-monospace fs-4"
                style={{ background: 'var(--bs-light)', border: '1px solid var(--bs-border-color)', minHeight: 40, color: 'var(--bs-body-color)' }}>
                {dialState || <span className="text-muted fs-7 fw-normal">Masukkan nomor</span>}
              </div>
            )}

            <div className="row g-2">
              {['1','2','3','4','5','6','7','8','9','*','0','#'].map(key => (
                <div className="col-4 d-flex justify-content-center" key={key}>
                  <button
                    className="btn fw-bold d-flex align-items-center justify-content-center rounded-3 fs-5"
                    style={{ width: '100%', height: dropUp ? 46 : 48, background: 'var(--bs-light)', border: '1px solid var(--bs-border-color)', color: 'var(--bs-body-color)' }}
                    onClick={() => onKeyPress(key)}
                  >
                    {key}
                  </button>
                </div>
              ))}
            </div>

            {dropUp && !isInCall && (
              <div className="d-flex gap-2 mt-3">
                <button
                  className="btn btn-light w-50 fw-bold rounded-3"
                  onClick={() => setDialState && setDialState((prev: string) => (prev || '').slice(0, -1))}
                >
                  Hapus
                </button>
                <button
                  className="btn btn-success w-50 fw-bold d-flex align-items-center justify-content-center gap-2 rounded-3"
                  style={{ background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none' }}
                  onClick={() => { onCall(); setShowDialpad(false); }}
                >
                  <Phone size={14} className="me-1" /> Panggil
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export { NavbarSoftphone };
